import React from "react";

import { css, keyframes } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import Slider from "react-slick";

import { media } from "@atlaskit/primitives";

import { Picture, PictureProps } from "@/components/wpl/image/src/image";
import { Card } from "@/libraries/design/components/card/src/card";
import { Link, LinkProps } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src/data-event-props";
import { Row } from "@/libraries/wpl/foundations/src/grid";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";
import { useIsMobile } from "@/utils/useIsMobile";

import type { Settings as SlickSettings } from "react-slick";

interface ContentList {
    headerText: string;
    headerType?: string;
    bodyText: string;
}

interface TabContentLinkProps {
    linkText: string;
    link: string;
    linkLabel: string;
    productKey: ProductKeyTypes;
}

export interface TabContentProps {
    contentList: ContentList[];
    contentLink?: TabContentLinkProps;
    pictureProps: PictureProps;
    tabContentEyebrowText?: string;
    cardsContent: CardContentProps[];
    className?: string;
}

const settings: SlickSettings = {
    arrows: false,
    centerMode: true,
    dots: true,
    infinite: false,
    swipeToSlide: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1
};

// Source https://github.com/kenwheeler/slick/blob/master/slick/slick.css
// FIXME: Nested selectors are used here to target dynamically inserted child elements
const slickStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-slider": {
        position: "relative",
        display: "block",
        boxSizing: "border-box",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        MsUserSelect: "none",
        userSelect: "none",
        WebkitTouchCallout: "none",
        KhtmlUserSelect: "none",
        MsTouchAction: "pan-y",
        touchAction: "pan-y",
        WebkitTapHighlightColor: "transparent"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-list": {
        position: "relative",
        overflow: "hidden",
        margin: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-track": {
        position: "relative",
        insetBlockStart: 0,
        insetInlineStart: 0,
        display: "block",
        marginInlineStart: "auto",
        marginInlineEnd: "auto"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-loading .slick-track": {
        visibility: "hidden"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-slide": {
        display: "none",
        float: "left",
        height: "100%",
        padding: "8px",
        minHeight: "1px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-initialized .slick-slide": {
        display: "block"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-dots": {
        border: 0,
        display: "flex !important",
        fontSize: 0,
        lineHeight: 0,
        padding: "5px",
        justifyContent: "center"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-dots li": {
        display: "inline-block",
        cursor: "pointer",
        height: "20px",
        width: "20px",
        margin: "0 5px",
        padding: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-dots li button": {
        border: 0,
        display: "block",
        fontSize: 0,
        lineHeight: 0,
        padding: "5px",
        cursor: "pointer",
        height: "20px",
        width: "20px",
        background: "transparent",
        color: "transparent",
        outline: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-dots li.slick-active button:before": {
        color: wacToken("color.primary.black"),
        opacity: "100%"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-dots li button:before": {
        color: wacToken("color.primary.black"),
        opacity: "50%",
        content: "•",
        fontSize: "35px",
        height: "20px",
        insetInlineStart: 0,
        textAlign: "center",
        insetBlockStart: 0,
        width: "20px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".slick-current": {
        display: "flex !important",
        justifyContent: "center"
    }
});

const TabsContent = (tabsContentProps: TabContentProps) => {
    const isMobile = useIsMobile();

    return (
        <TypographyStyles>
            <div
                css={wrapperStyles}
                className={tabsContentProps.className || ""}
            >
                <Row>
                    <div css={contentListWrapperStyle}>
                        <div css={contentListTextWrapperStyle}>
                            <div
                                css={contentListStyles}
                                className="content-list"
                            >
                                {tabsContentProps.contentList.map(
                                    (content, index) => (
                                        <div
                                            key={"content-" + index}
                                            css={ContentTextStyles}
                                            className="content-text"
                                        >
                                            <h3
                                                css={[contentListHeaderStyle]}
                                                className={content?.headerType}
                                            >
                                                {content.headerText}
                                            </h3>
                                            <p css={ContentTextPStyle}>
                                                {content.bodyText}
                                            </p>
                                        </div>
                                    )
                                )}
                            </div>
                            {tabsContentProps.contentLink && (
                                <div
                                    css={linkContainerStyle}
                                    className="link-content-container"
                                >
                                    <Link
                                        href={tabsContentProps.contentLink.link}
                                        text={
                                            tabsContentProps.contentLink
                                                ?.linkText
                                        }
                                        type={"primary"}
                                        dataEventProps={{
                                            "data-product-key":
                                                tabsContentProps.contentLink
                                                    ?.productKey,
                                            "data-event": "clicked",
                                            "data-event-component":
                                                "linkButton",
                                            "data-event-container":
                                                "tabsSection",
                                            "data-label-english":
                                                tabsContentProps.contentLink
                                                    ?.linkLabel
                                        }}
                                        aria-label={
                                            tabsContentProps.contentLink
                                                ?.linkText
                                        }
                                    />
                                </div>
                            )}
                        </div>
                        <div css={bigImgWrapperStyles}>
                            <Picture
                                {...tabsContentProps.pictureProps}
                                css={contentImageStyle}
                            />
                        </div>
                    </div>
                </Row>
                {tabsContentProps.tabContentEyebrowText && (
                    <Row>
                        <div className="tab-content-header-wrapper">
                            <h2 className="eyebrow-md" css={eyebrowStyle}>
                                {tabsContentProps.tabContentEyebrowText}
                            </h2>
                        </div>
                    </Row>
                )}
                <div className="tabs-content-cards">
                    {isMobile ? (
                        <div css={slickStyle}>
                            <Slider {...settings}>
                                {tabsContentProps.cardsContent.map(
                                    (cardContent, index) => (
                                        <React.Fragment key={"card-" + index}>
                                            <TabCard {...cardContent} />
                                        </React.Fragment>
                                    )
                                )}
                            </Slider>
                        </div>
                    ) : (
                        <Row layout="4-4-4" containerType="ul">
                            {tabsContentProps.cardsContent.map(
                                (cardContent, index) => (
                                    <React.Fragment key={"card-" + index}>
                                        <TabCard
                                            {...cardContent}
                                            containerType="li"
                                        />
                                    </React.Fragment>
                                )
                            )}
                        </Row>
                    )}
                </div>
            </div>
        </TypographyStyles>
    );
};

const slidein = keyframes({
    from: {
        opacity: 0,
        transform: "translateX(-100px)"
    },
    to: {
        opacity: 1,
        transform: "translateX(0)"
    }
});

const wrapperStyles = css({
    animation: `${slidein} linear 500ms 1`
});

const bigImgWrapperStyles = css({
    overflow: "hidden",

    [media.above.md]: {
        gridColumnStart: 5,
        gridColumnEnd: -1
    },
    [media.below.md]: {
        marginBlockStart: 20
    }
});

const contentListWrapperStyle = css({
    [media.above.md]: {
        display: "grid",
        gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
        gap: 32
    }
});

const contentListTextWrapperStyle = css({
    [media.above.md]: {
        gridColumnStart: 1,
        gridColumnEnd: 5
    }
});

const contentListStyles = css({
    [media.only.sm]: {
        display: "flex",
        gap: "32px"
    }
});

const contentImageStyle = css({
    // Picture component does not accept compiled CSS. Using nested selector to apply CSS to displayed image.
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        maxWidth: "100%",
        objectFit: "contain",
        width: "100%",
        alignSelf: "baseline",
        height: "auto",
        animation: `${slidein} linear 500ms 1`,

        [media.below.sm]: {
            marginBlockStart: "6px"
        }
    }
});

const eyebrowStyle = css({
    textAlign: "center",
    marginBlockStart: 0,
    marginBlockEnd: 24
});

const ContentTextStyles = css({
    [media.below.sm]: {
        paddingBlockEnd: 16
    },
    [media.above.md]: {
        "paddingBlockEnd": 8,
        "marginBlockEnd": 24,
        "borderBlockEnd": `1.5px dashed ${wacToken("color.neutral.light400")}`,
        "&:only-of-type": {
            borderBlockEnd: "none"
        }
    }
});

const ContentTextPStyle = css({
    [media.below.sm]: {
        marginBlockStart: 8
    }
});

const contentListHeaderStyle = css({
    marginBlockEnd: 0,
    marginBlockStart: 0
});

const linkContainerStyle = css({
    [media.only.sm]: {
        textAlign: "center"
    }
});

interface CardContentProps {
    eyebrowText?: string;
    headerText: string | React.ReactElement<typeof FormattedMessage>;
    bodyText?: string;
    ctaProps?: LinkProps;
    pictureProps: PictureProps;
    containerType?: keyof JSX.IntrinsicElements;
}

const TabCard = (cardContentProps: CardContentProps) => {
    const {
        eyebrowText,
        headerText,
        bodyText,
        ctaProps,
        pictureProps,
        containerType
    } = cardContentProps;

    return (
        <Card
            title={headerText}
            titleAs="h3"
            image={pictureProps.image}
            body={bodyText}
            eyebrow={eyebrowText}
            callToAction={ctaProps}
            containerType={containerType}
        />
    );
};

export default TabsContent;
